import { Injectable, signal, computed } from '@angular/core';
import commandScore from 'command-score';

export interface Command {
  readonly id: string;
  readonly label: string;
  readonly category: 'navigation' | 'action' | 'settings';
  readonly keywords: readonly string[];
  readonly icon?: string;
  readonly shortcut?: string;
  readonly requiresCluster?: boolean;
  readonly requiresWorkspace?: boolean;
  /** Only offered while a ResourceScope is set; the palette shows that scope next to the command. */
  readonly requiresResourceScope?: boolean;
  readonly execute: () => void;
}

/**
 * Where resource commands and the resource search operate. A selected workspace wins
 * for everyone, admins included: inside a workspace the palette shows that workspace's
 * resources, never the cluster's. The cluster scope applies only outside a workspace
 * and only to users clusterPermissionResolver lets in (organization canChange), so a
 * workspace user never gets a target that rejects them.
 */
export interface ResourceScope {
  readonly kind: 'cluster' | 'workspace';
  readonly clusterId: string;
  /** Workspace name for 'workspace', cluster display name for 'cluster'. */
  readonly name: string;
}

export function resolveResourceScope(
  cluster: { id?: string; name?: string } | null,
  workspaceName: string | null | undefined,
  canChangeOrganization: boolean
): ResourceScope | null {
  if (!cluster?.id) return null;
  if (workspaceName) return { kind: 'workspace', clusterId: cluster.id, name: workspaceName };
  // Same gate as clusterPermissionResolver on cluster/:id/resources.
  if (canChangeOrganization) return { kind: 'cluster', clusterId: cluster.id, name: cluster.name ?? '' };
  return null;
}

/** The resource list page of a scope; the resource manager picks kind and item from sm_* query params. */
export function resourceScopeRoute(orgId: string, scope: ResourceScope): string[] {
  const cluster = ['/', 'studio', 'organization', orgId, 'cluster', scope.clusterId];
  return scope.kind === 'workspace' ? [...cluster, 'workspaces', scope.name, 'resources'] : [...cluster, 'resources'];
}

interface CommandHistoryEntry {
  commandId: string;
  lastUsed: number;
  useCount: number;
}

const MRU_STORAGE_KEY = 'mogenius_cmd_history';
const MRU_MAX_ITEMS = 10;

/**
 * Entry points offered when the palette opens with an empty query, so someone
 * without history lands on something actionable instead of the full command list.
 *
 * Ids only: these commands are registered elsewhere and never duplicated here. An
 * id that is not registered, or that the current context filters out, is skipped.
 *
 * TODO(design): final selection and order of the suggested entry points is open.
 * An organization-level "Notifications" entry was asked for but has no command
 * today — notifications exist only as cluster- and workspace-scoped routes.
 */
const ZERO_QUERY_SUGGESTION_IDS: readonly string[] = ['nav:dashboard', 'nav:clusters', 'nav:workspaces'];

export interface ResourceResult {
  readonly name: string;
  readonly namespace: string;
  readonly kind: string;
  readonly apiVersion: string;
  readonly icon: string;
  /** The scope the result was found in; quick actions build their routes from it. */
  readonly scope: ResourceScope;
  readonly execute: () => void;
}

export type ClusterPage = 'overview' | 'settings';
export type WorkspacePage = 'dashboard' | 'resources';

export interface NavigationResult {
  readonly name: string;
  readonly type: 'cluster' | 'workspace';
  readonly icon: string;
  readonly parentName?: string;
  readonly score?: number;
  readonly clusterId?: string;
  readonly execute: () => void;
  /**
   * Opens a page of this cluster or workspace. Quick actions go through here rather than
   * building the route themselves: the result can belong to a cluster other than the
   * selected one, and workspace routes only resolve once that cluster is selected.
   */
  readonly openPage: (page: ClusterPage | WorkspacePage) => void;
}

export interface CommandGroup {
  readonly category: string;
  readonly commands: Command[];
}

@Injectable({ providedIn: 'root' })
export class CommandPaletteService {
  private readonly _commands = signal<Command[]>([]);
  private readonly _isOpen = signal(false);
  private readonly _closing = signal(false);
  private readonly _query = signal('');
  private readonly _resourceResults = signal<ResourceResult[]>([]);
  private readonly _navigationResults = signal<NavigationResult[]>([]);
  private readonly _resourceLoading = signal(false);
  private readonly _history = signal<CommandHistoryEntry[]>(this.loadHistory());
  private readonly _hasCluster = signal(false);
  private readonly _hasWorkspace = signal(false);
  private readonly _resourceScope = signal<ResourceScope | null>(null);

  readonly isOpen = this._isOpen.asReadonly();
  readonly resourceScope = this._resourceScope.asReadonly();
  readonly closing = this._closing.asReadonly();
  readonly query = this._query.asReadonly();
  readonly resourceResults = this._resourceResults.asReadonly();
  readonly navigationResults = this._navigationResults.asReadonly();
  readonly resourceLoading = this._resourceLoading.asReadonly();

  private readonly availableCommands = computed(() => {
    const commands = this._commands();
    const hasCluster = this._hasCluster();
    const hasWorkspace = this._hasWorkspace();
    const hasResourceScope = !!this._resourceScope();

    return commands.filter((cmd) => {
      if (cmd.requiresCluster && !hasCluster) return false;
      if (cmd.requiresWorkspace && !hasWorkspace) return false;
      if (cmd.requiresResourceScope && !hasResourceScope) return false;
      return true;
    });
  });

  readonly filteredCommands = computed(() => {
    const q = this._query().trim();
    const commands = this.availableCommands();
    const history = this._history();

    if (!q) {
      // No query: boost recent commands to top, then show rest by category
      const recentIds = new Set(history.map((h) => h.commandId));
      const recentCommands = history
        .map((h) => commands.find((c) => c.id === h.commandId))
        .filter((c): c is Command => !!c);
      const otherCommands = commands.filter((c) => !recentIds.has(c.id));
      return [...recentCommands, ...otherCommands];
    }

    // With query: fuzzy search with MRU boost
    const historyMap = new Map(history.map((h, i) => [h.commandId, history.length - i]));

    return commands
      .map((cmd) => {
        const labelScore = commandScore(cmd.label, q);
        const keywordScore = cmd.keywords.length > 0 ? commandScore(cmd.keywords.join(' '), q) * 0.8 : 0;
        const baseScore = Math.max(labelScore, keywordScore);
        // Boost recent commands slightly (max 10% boost for most recent)
        const mruBoost = (historyMap.get(cmd.id) ?? 0) * 0.01;
        return { cmd, score: baseScore + (baseScore > 0 ? mruBoost : 0) };
      })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((entry) => entry.cmd)
      .slice(0, 10);
  });

  readonly recentCommands = computed(() => {
    const commands = this.availableCommands();
    const history = this._history();
    return history
      .map((h) => commands.find((c) => c.id === h.commandId))
      .filter((c): c is Command => !!c)
      .slice(0, 5);
  });

  /**
   * The zero-query entry points, in the order declared above and without anything
   * the Recent group already shows.
   */
  readonly suggestedCommands = computed<Command[]>(() => {
    const commands = this.availableCommands();
    const recentIds = new Set(this.recentCommands().map((c) => c.id));

    return ZERO_QUERY_SUGGESTION_IDS.map((id) => commands.find((c) => c.id === id)).filter(
      (c): c is Command => !!c && !recentIds.has(c.id)
    );
  });

  readonly groupedCommands = computed<CommandGroup[]>(() => {
    const q = this._query().trim();
    const commands = this.filteredCommands();
    const groups: CommandGroup[] = [];

    // Show "Recent" group only when no query and we have recent commands
    if (!q) {
      const recent = this.recentCommands();
      if (recent.length > 0) {
        groups.push({ category: 'Recent', commands: recent });
      }

      const suggested = this.suggestedCommands();
      if (suggested.length > 0) {
        groups.push({ category: 'Suggested', commands: suggested });
      }

      // Show other commands grouped by category (excluding what is already above)
      const shownIds = new Set([...recent, ...suggested].map((c) => c.id));
      const categoryOrder = ['navigation', 'action', 'settings'] as const;
      for (const cat of categoryOrder) {
        const cmds = commands.filter((c) => c.category === cat && !shownIds.has(c.id));
        if (cmds.length > 0) {
          groups.push({
            category: cat.charAt(0).toUpperCase() + cat.slice(1),
            commands: cmds
          });
        }
      }
    } else {
      // With query: group filtered results by category
      const categoryOrder = ['navigation', 'action', 'settings'] as const;
      for (const cat of categoryOrder) {
        const cmds = commands.filter((c) => c.category === cat);
        if (cmds.length > 0) {
          groups.push({
            category: cat.charAt(0).toUpperCase() + cat.slice(1),
            commands: cmds
          });
        }
      }
    }

    return groups;
  });

  readonly hasResults = computed(() => {
    return (
      this.groupedCommands().length > 0 || this.resourceResults().length > 0 || this.navigationResults().length > 0
    );
  });

  public register(...commands: Command[]): void {
    this._commands.update((existing) => {
      const ids = new Set(commands.map((c) => c.id));
      const filtered = existing.filter((c) => !ids.has(c.id));
      return [...filtered, ...commands];
    });
  }

  public unregister(...ids: string[]): void {
    const idSet = new Set(ids);
    this._commands.update((cmds) => cmds.filter((c) => !idSet.has(c.id)));
  }

  public open(): void {
    this._query.set('');
    this._resourceResults.set([]);
    this._navigationResults.set([]);
    this._isOpen.set(true);
  }

  public close(): void {
    if (this._closing()) return;
    this._closing.set(true);
    setTimeout(() => {
      this._isOpen.set(false);
      this._closing.set(false);
      this._query.set('');
      this._resourceResults.set([]);
      this._navigationResults.set([]);
    }, 150);
  }

  public toggle(): void {
    if (this._isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  public setQuery(query: string): void {
    this._query.set(query);
  }

  public setResourceResults(results: ResourceResult[]): void {
    this._resourceResults.set(results);
  }

  public setResourceLoading(loading: boolean): void {
    this._resourceLoading.set(loading);
  }

  public setNavigationResults(results: NavigationResult[]): void {
    this._navigationResults.set(results);
  }

  public setContext(hasCluster: boolean, hasWorkspace: boolean): void {
    this._hasCluster.set(hasCluster);
    this._hasWorkspace.set(hasWorkspace);
  }

  public setResourceScope(scope: ResourceScope | null): void {
    this._resourceScope.set(scope);
  }

  public execute(command: Command): void {
    this.close();
    this.recordUsage(command.id);
    command.execute();
  }

  public executeResource(result: ResourceResult): void {
    this.close();
    result.execute();
  }

  private recordUsage(commandId: string): void {
    const now = Date.now();
    this._history.update((history) => {
      const existing = history.find((h) => h.commandId === commandId);
      const updated: CommandHistoryEntry[] = existing
        ? history.map((h) => (h.commandId === commandId ? { ...h, lastUsed: now, useCount: h.useCount + 1 } : h))
        : [{ commandId, lastUsed: now, useCount: 1 }, ...history];

      // Sort by lastUsed (most recent first) and limit to max items
      const sorted = updated.sort((a, b) => b.lastUsed - a.lastUsed).slice(0, MRU_MAX_ITEMS);
      this.saveHistory(sorted);
      return sorted;
    });
  }

  private loadHistory(): CommandHistoryEntry[] {
    try {
      const stored = localStorage.getItem(MRU_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  private saveHistory(history: CommandHistoryEntry[]): void {
    try {
      localStorage.setItem(MRU_STORAGE_KEY, JSON.stringify(history));
    } catch {
      // LocalStorage full or unavailable — ignore
    }
  }

  public clearHistory(): void {
    this._history.set([]);
    try {
      localStorage.removeItem(MRU_STORAGE_KEY);
    } catch {
      // Ignore
    }
  }
}
